import {USER_INFO_COOKIE_NAME, AUTH_TOKEN_EXPIRY_BUFFER_MS} from '~/conf/app.js';

function jsonDecode(val) {
  if (!val) return null;
  if (typeof val === 'object') return val;
  try { return JSON.parse(decodeURIComponent(val)); } catch { /* */ }
  try { return JSON.parse(val); } catch { return null; }
}

function jsonEncode(val) {
  return encodeURIComponent(JSON.stringify(val));
}

const LOCAL_HOSTNAME = /^localhost$|^\d{1,3}(\.\d{1,3}){3}$/;
const SHARED_COOKIE_DOMAINS = ['sandbox4ever.com', 'staging4ever.com', 'forever.com'];

function toSharedDomain(hostname) {
  if (!hostname || LOCAL_HOSTNAME.test(hostname)) return undefined;
  const normalized = hostname.startsWith('.') ? hostname.slice(1) : hostname;
  const sharedDomain = SHARED_COOKIE_DOMAINS.find(domain => normalized === domain || normalized.endsWith(`.${domain}`));
  return sharedDomain ? `.${sharedDomain}` : undefined;
}

// Derives the shared cookie domain (.sandbox4ever.com) from whichever source is
// available: explicit sharedCookieDomain config, browser hostname, or SSR request hostname.
function getSharedDomain() {
  const configured = useRuntimeConfig()?.public?.sharedCookieDomain;
  const configuredDomain = toSharedDomain(configured);
  if (configuredDomain) return configuredDomain;

  if (typeof document !== 'undefined') {
    return toSharedDomain(document.location.hostname);
  }
  return toSharedDomain(useRequestURL()?.hostname);
}

export function useAuthCookie() {
  const domain = getSharedDomain();
  const userInfoCookie = useCookie(USER_INFO_COOKIE_NAME, {
    path: '/',
    sameSite: 'lax',
    secure: !!domain,
    ...(domain ? {domain} : {}),
    decode: jsonDecode,
    encode: jsonEncode
  });

  // SSR-only in-memory mirror of the user_info cookie. On the server, a refreshed token is
  // appended to the response as a raw Set-Cookie header (see token-refresh.js), which
  // useCookie can't see mid-request — so http.js/middleware need this to get the fresh
  // value within the same SSR request. On the client the cookie itself is always current
  // (and shared across tabs), so nothing should read or write this after hydration.
  const authState = useState('auth.userInfo', () => null);

  return {
    userInfoCookie,
    authState,
    // Use this instead of authState/userInfoCookie directly when just reading the
    // current token: SSR reads the in-memory mirror, client always reads the cookie fresh.
    currentUserInfo() {
      return process.server ? authState.value : userInfoCookie.value;
    },
    // Client-only. Clears both the shared-domain cookie and any host-only copy (the same
    // pair server/api/auth/refresh.post.js cleans up). Written to document.cookie directly
    // rather than via userInfoCookie, whose write is deferred to a watcher and could be
    // skipped by an immediate navigation.
    clearUserInfoCookie() {
      if (process.server) return;
      const expired = `${USER_INFO_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      if (domain) document.cookie = `${expired}; domain=${domain}`;
      document.cookie = expired;
    },
    isTokenExpired(userInfo) {
      const expiresAt = userInfo?.auth_token_expires_at;
      if (!expiresAt) return true;
      return new Date(expiresAt).getTime() - Date.now() < AUTH_TOKEN_EXPIRY_BUFFER_MS;
    }
  };
}
